import { Box, FormGroup, NumberInput, Text } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ServiceSettings } from '../interfaces/serviceSettings';
import { translateKey } from '../../../../../translate';
import { DropdownInput } from '../../../../fields/Dropdown';
import { SettingAnswerTypes } from '../../../carePath/constants/settingsLists';
import { SettingsSwitch } from '../../../carePath/modals/components/SettingsSwitch';
import { generateOptions } from '../../../customControls/utils/v1/generateOptions';

interface ISharedServiceSettingsProps<T extends ServiceSettings> {
    currentSettings: T;
    errorPrefix: string;
    errors: ValidationErrors;
    list: { [key in T as string]: any };
    listKey: keyof ServiceSettings;
    onSettingsChange: (value: boolean | string | number | null, listKey: keyof ServiceSettings) => void;
    requiredProperties?: string[];
}

export function SharedServiceSettings<T extends ServiceSettings>(props: ISharedServiceSettingsProps<T>) {
    return (
        <FormGroup
            isRequired={props.requiredProperties?.includes(props.listKey)}
            key={props.currentSettings.id + props.listKey}
            label={props.list[props.listKey].label}
        >
            {props.list[props.listKey as string].type === SettingAnswerTypes.boolean && (
                <SettingsSwitch
                    error={getErrorMessage(props.errors, props.errorPrefix, props.listKey)}
                    listKey={props.listKey}
                    onChange={(value) => props.onSettingsChange(value, props.listKey)}
                    value={convertToBooleanOrUndefined(props.currentSettings[props.listKey])}
                />
            )}

            {props.list[props.listKey].type === SettingAnswerTypes.dropdown && (
                <DropdownInput
                    error={props.errors.get(`${props.errorPrefix}.${props.listKey}`)}
                    onChange={(value) => props.onSettingsChange(value, props.listKey)}
                    placeholder={translateKey('common.clickAndChooseFromList')}
                    selectOptions={generateOptions(props.list[props.listKey].options)}
                    value={props.currentSettings[props.listKey] as string}
                />
            )}

            {props.list[props.listKey].type === SettingAnswerTypes.input && (
                <Box display="flex">
                    <Box pl={3}>
                        <NumberInput
                            error={getErrorMessage(props.errors, props.errorPrefix, props.listKey)}
                            label={translateKey('embloomQuestionnaires.weeks')}
                            onChange={(value) => props.onSettingsChange(value, props.listKey)}
                            value={props.currentSettings[props.listKey] as unknown as number}
                        />
                    </Box>
                    <Box
                        alignSelf="center"
                        ml={2}
                    >
                        <Text size="sm">{translateKey('embloomQuestionnaires.weeks')}</Text>
                    </Box>
                </Box>
            )}
        </FormGroup>
    );

    function convertToBooleanOrUndefined(value: boolean | string) {
        return typeof value === 'boolean' ? Boolean(value) : undefined;
    }

    function getErrorMessage(errors: ValidationErrors, errorPrefix: string, listKey: string) {
        const errorMessage = errors.get(`${errorPrefix}.${listKey}`);

        if (errorMessage) {
            return translateKey(`validation.${errorMessage}`);
        }
    }
}
